import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Pill, SurfacePagePanel, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import {
  STAGE_INK_LINK,
  STAGE_LABEL_CAPS,
  STAGE_ROW_META,
  STAGE_ROW_SELECTED,
  STAGE_ROW_TITLE,
  STAGE_TABLE,
  STAGE_TABLE_SCROLL,
  STAGE_TD,
  STAGE_TD_FIRST,
  STAGE_TH,
  STAGE_TH_FIRST,
  STAGE_THEAD,
} from '@/versions/v1/modules/advisory/stage-body-ui';
import {
  catalogueMeta,
  earlierRunsLabel,
  runCountLine,
  runsGroupLabel,
  workflowRunGroups,
  type WorkflowCatalogueRow,
  type WorkflowRunColumns,
  type WorkflowRunRow,
} from './WorkflowCatalogue.transform';
import { useWorkflowCatalogueRows } from './useWorkflowCatalogueRows';

const WORKFLOW_PARAM = 'workflow';

/**
 * K19: the project's Workflows page for the OptiFlow plant — every prewritten
 * definition of docs/engines/workflows.md § Catalogue with the runs it has at
 * the replay cursor. A row opens the definition's runs below the catalogue;
 * each run opens the object it wrote (the case, its trace, the report, the rule set).
 */
export function WorkflowCatalogue({ projectId }: { projectId: string }) {
  const { state } = useScenario();
  const rows = useWorkflowCatalogueRows(projectId);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get(WORKFLOW_PARAM);
  const selected = rows.find((row) => row.id === selectedId) ?? null;
  const runsRef = useRef<HTMLDivElement>(null);

  // Opening a workflow brings its runs into view under the catalogue.
  useEffect(() => {
    if (!selected || !runsRef.current) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    runsRef.current.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });
    // A deep link lands before the replay frames do; follow the panel as its runs arrive.
  }, [selected?.id, selected?.runCount]);

  const select = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(WORKFLOW_PARAM, id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-3">
      <Commentable name="WorkflowCatalogue">
        <SurfacePagePanel
          title="Catalogue"
          meta={catalogueMeta(rows.length, state.cursor)}
          flush
        >
          <Table clickableRows wrap={false} className={STAGE_TABLE}>
            <THead className={STAGE_THEAD}>
              <TR>
                <TH className={STAGE_TH_FIRST}>Workflow</TH>
                <TH className={STAGE_TH}>Verb</TH>
                <TH className={STAGE_TH}>Who acts</TH>
                <TH className={STAGE_TH}>Trigger</TH>
                <TH className={STAGE_TH}>Writes</TH>
                <TH className={STAGE_TH}>Last run</TH>
                <TH className={cn(STAGE_TH, 'text-right')}>Runs</TH>
              </TR>
            </THead>
            <TBody data-workflow-catalogue>
              {rows.map((row) => {
                const isSelected = row.id === selected?.id;
                return (
                  <TR
                    key={row.id}
                    data-workflow-row={row.id}
                    data-selected={isSelected || undefined}
                    aria-selected={isSelected}
                    className={isSelected ? STAGE_ROW_SELECTED : undefined}
                    onRowClick={() => select(row.id)}
                  >
                    <TD className={cn(STAGE_TD_FIRST, 'min-w-56 align-top')}>
                      <div className={STAGE_ROW_TITLE}>{row.name}</div>
                      <div className={STAGE_ROW_META}>{row.id}</div>
                    </TD>
                    <TD className={cn(STAGE_TD, 'align-top')}>
                      <span className={cn(STAGE_LABEL_CAPS, 'text-[10px] leading-5')}>{row.verb}</span>
                    </TD>
                    <TD className={cn(STAGE_TD, 'whitespace-nowrap align-top')}>
                      <span className="inline-flex pt-0.5"><ActorChip actor={row.actor} /></span>
                    </TD>
                    <TD className={cn(STAGE_TD, 'align-top text-xs leading-snug text-muted')}>
                      {row.triggers.map((trigger) => (
                        <div key={`${trigger.kind}-${trigger.label}`} className="whitespace-nowrap">
                          <span className="font-semibold text-ink-secondary">{trigger.kind}</span>
                          {trigger.label ? ` · ${trigger.label}` : ''}
                        </div>
                      ))}
                    </TD>
                    <TD className={cn(STAGE_TD, 'align-top text-xs leading-snug text-ink-secondary')}>{row.writes}</TD>
                    <TD
                      className={cn(STAGE_TD, 'whitespace-nowrap align-top font-mono text-xs tabular-nums', row.lastRunLabel ? 'text-ink-secondary' : 'text-muted')}
                      aria-busy={row.loading || undefined}
                    >
                      {row.loading ? null : row.lastRunLabel ?? 'none'}
                    </TD>
                    <TD
                      className={cn(STAGE_TD, 'align-top text-right text-sm font-semibold tabular-nums text-ink-primary')}
                      data-workflow-run-count
                      data-loading={row.loading || undefined}
                      aria-busy={row.loading || undefined}
                    >
                      {row.loading ? null : row.runCount}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </SurfacePagePanel>
      </Commentable>

      {selected ? (
        <div ref={runsRef} className="scroll-mb-4">
          <Commentable name="WorkflowRuns">
            <WorkflowRunsPanel row={selected} />
          </Commentable>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The selected workflow's runs. The panel carries no count and no window: the
 * row's Runs cell and the catalogue meta say those once. When the window holds
 * no run, the one run Last run names (dated, before the window) is listed under
 * a group line saying so, so it is never read as that window's runs.
 */
function WorkflowRunsPanel({ row }: { row: WorkflowCatalogueRow }) {
  const groups = workflowRunGroups(row.runs.length > 0 ? row.runs : row.earlierRun ? [row.earlierRun] : []);
  const earlierLabel = runsGroupLabel(row);
  // A table that follows a group line repeats its column heads (a later table in the panel).
  const grouped = groups.some((group) => group.label);

  return (
    <SurfacePagePanel title={row.name} flush>
      <div data-workflow-runs={row.id} aria-busy={row.loading || undefined}>
        {row.facts.length > 0 ? (
          <dl className="hairline-b flex flex-wrap gap-x-6 gap-y-1 px-4 py-2 text-xs leading-relaxed">
            {row.facts.map((fact) => (
              <div key={fact.label} className="flex min-w-0 gap-2">
                <dt className="shrink-0 font-semibold text-ink-secondary">{fact.label}</dt>
                <dd className="min-w-0 text-muted" data-workflow-fact={fact.label}>{fact.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {groups.length > 0 ? (
          <div className={cn(STAGE_TABLE_SCROLL, 'max-h-[24rem]')}>
            {groups.map((group, index) => {
              // The group's own line, or the earlier-run line when the window is empty.
              const headerLabel = group.label ?? earlierLabel;
              return (
                <div key={group.key || 'runs'} className={cn(index > 0 && 'hairline-t')} data-workflow-run-group={group.key || undefined}>
                  {headerLabel !== null ? (
                    <div className="flex items-baseline gap-2 px-4 pb-1 pt-2.5" data-workflow-run-group-label>
                      <span className="font-mono text-xs font-semibold text-ink-primary">{headerLabel}</span>
                      {group.meta ? <span className="min-w-0 truncate text-xs text-ink-secondary">{group.meta}</span> : null}
                      {group.label ? <span className="ml-auto shrink-0 text-xs tabular-nums text-muted">{runCountLine(group.count)}</span> : null}
                    </div>
                  ) : null}
                  <RunsTable runs={group.latest} columns={row.runColumns} showHead={index === 0 || grouped} />
                  {group.earlier.length > 0 ? (
                    <details className="group" data-workflow-earlier-runs={group.key || 'runs'}>
                      <summary className="flex cursor-pointer list-none items-center gap-1.5 px-4 py-2 text-xs font-medium text-ink-secondary transition-colors duration-150 hover:bg-[var(--surface-1)] [&::-webkit-details-marker]:hidden">
                        <ChevronRight aria-hidden className="size-3.5 transition-transform duration-150 group-open:rotate-90 motion-reduce:transition-none" />
                        {earlierRunsLabel(group.earlier.length)}
                      </summary>
                      <RunsTable runs={group.earlier} columns={row.runColumns} showHead={false} />
                    </details>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </SurfacePagePanel>
  );
}

/** Fixed column widths so a group's shown rows and its folded rows line up; the second set drops the subject column. */
const RUN_COLUMN_WIDTHS = ['w-[14%]', 'w-[32%]', 'w-[12%]', 'w-[16%]', 'w-[16%]', 'w-[10%]'];
const RUN_COLUMN_WIDTHS_NO_SUBJECT = ['w-[24%]', 'w-[22%]', 'w-[22%]', 'w-[18%]', 'w-[14%]'];

function RunsTable({ runs, columns, showHead }: { runs: readonly WorkflowRunRow[]; columns: WorkflowRunColumns; showHead: boolean }) {
  const widths = columns.subject === null ? RUN_COLUMN_WIDTHS_NO_SUBJECT : RUN_COLUMN_WIDTHS;
  return (
    <Table wrap={false} className={cn(STAGE_TABLE, 'table-fixed')}>
      <colgroup>
        {widths.map((width, index) => <col key={index} className={width} />)}
      </colgroup>
      {showHead ? (
        <THead className={STAGE_THEAD}>
          <TR>
            <TH className={STAGE_TH_FIRST}>{columns.id}</TH>
            {columns.subject !== null ? <TH className={STAGE_TH}>{columns.subject}</TH> : null}
            <TH className={STAGE_TH}>Started</TH>
            <TH className={STAGE_TH}>Status</TH>
            <TH className={cn(STAGE_TH, 'text-right')}>{columns.detail}</TH>
            <TH className={cn(STAGE_TH, 'pr-4 text-right')} aria-label="Open" />
          </TR>
        </THead>
      ) : null}
      <TBody data-workflow-run-list>
        {runs.map((run) => (
          <TR key={`${run.id}-${run.at}`} data-workflow-run={run.id}>
            <TD className={cn(STAGE_TD_FIRST, 'truncate whitespace-nowrap font-mono text-xs font-semibold text-ink-primary')}>{run.id}</TD>
            {columns.subject !== null ? (
              <TD className={cn(STAGE_TD, 'min-w-0')}>
                <div className="text-sm leading-snug text-ink-primary">{run.subject}</div>
                {run.subjectMeta ? <div className={STAGE_ROW_META}>{run.subjectMeta}</div> : null}
              </TD>
            ) : null}
            <TD className={cn(STAGE_TD, 'whitespace-nowrap font-mono text-xs tabular-nums text-muted')}>{run.atLabel}</TD>
            <TD className={cn(STAGE_TD, 'whitespace-nowrap')}>
              <Pill tone={run.tone} className="px-1.5 text-[10px]">{run.status}</Pill>
            </TD>
            <TD className={cn(STAGE_TD, 'whitespace-nowrap text-right text-xs tabular-nums text-ink-secondary')}>{run.detail}</TD>
            <TD className={cn(STAGE_TD, 'whitespace-nowrap pr-4 text-right')}>
              <Link to={run.href} className={STAGE_INK_LINK} data-workflow-run-link={run.id}>
                {run.linkLabel}
              </Link>
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
